import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";

import { StatusCode } from "api/protocols";

import { showError } from "redux/actions/ui";
import { ILocation, IModule } from "redux/reducers/models/reservations";
import { useAppDispatch, useAppSelector } from "hooks/redux";

import Page from "components/page/Page";
import DataTable from "../../../../../components/dataTable/DataTable";
import {
  GetAllReservationLocation,
  GetReservationModuleAll,
} from "api/rpc/2024-04/facilityAdmin/reservation/reservation";

export default function Modules() {
  const history = useHistory();
  const { t } = useTranslation();
  const { authStore } = useAppSelector(store => store);

  const dispatch = useAppDispatch();
  const [modules, setModules] = useState<Array<IModule & { locations?: Array<ILocation> }>>(undefined);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadAllReservationModules(source.token);
    return () => source.cancel();
  }, []);

  async function loadAllReservationModules(token?: CancelToken) {
    if (modules !== undefined) {
      setModules(undefined);
    }
    const [moduleRes, locationRes] = await Promise.all([
      GetReservationModuleAll(null, token ? false : true, token),
      GetAllReservationLocation(token ? false : true, token),
    ]);

    if (token && token.reason) {
      return;
    }
    if (moduleRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading modules")));
      setModules([]);
      return;
    }

    if (locationRes?.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading locations")));
      setModules([]);
      return;
    }

    const locations = [...locationRes?.data];

    const modulesWithLocations = moduleRes?.data?.reduce((updatedModules, currentModule) => {
      const foundLocations: Array<ILocation> = locations?.filter(
        location => location?.reservation_module_id === currentModule?.id,
      );
      const updatedModule: IModule & { locations: Array<ILocation> } = { ...currentModule, locations: foundLocations };
      return [...updatedModules, updatedModule];
    }, [] as Array<IModule & { locations: Array<ILocation> }>);
    setModules(modulesWithLocations);
  }

  return (
    <Page
      title={t("Modules")}
      subtitle={t("Manage Modules")}
      narrow
      primaryAction={{
        content: t("New Module"),
        action: () => history.push("/admin/settings/reservations/module/new"),
        disabled: !authStore?.user?.permissions?.reservations_module_new,
      }}
      multipleActionDropdownAction={{
        label: t("Options"),
        dropdownProps: {
          alignment: "right",
          options: [
            {
              type: "handler",
              label: t("New Locations"),
              icon: "plus",
              handler: () => history.push("/admin/settings/reservations/locations/new"),
              disabled: !authStore?.user?.permissions?.reservations_location_new,
            },
          ],
        },
      }}
    >
      <DataTable
        columns={[{ label: t("Title") }]}
        loading={modules === undefined}
      >
        {modules?.map(module => (
          <React.Fragment key={module.id}>
            <tr
              onClick={() => history.push("/admin/settings/reservations/module/" + String(module.id))}
              className="clickable"
            >
              <td>{module.title}</td>
            </tr>
            {module?.locations?.length > 0
              ? module?.locations?.map((location, index) => {
                  return (
                    <tr key={index} className="clickable">
                      <td
                        style={{ textIndent: "4rem" }}
                        onClick={() => history.push(`/admin/settings/reservations/locations/${String(location.id)}`)}
                      >
                        {location?.title}
                      </td>
                    </tr>
                  );
                })
              : null}
          </React.Fragment>
        ))}
      </DataTable>
    </Page>
  );
}
